import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import { ErrorRequest } from '../../constants/errorRequest';
import { RootState } from '../../store/store';
import { setLoadingCall } from '../../store/slices/app/app';
import {
  selectCurrentUser,
  setTokenAndRefreshToken,
  setCredentials
} from '../../store/slices/login/login';

const mutex = new Mutex();

type RefreshApiTokenResponse = {
  id: number;
  refreshToken: string;
  username: string;
  token: string;
};

const customFetchBase = (baseUrl: string) => {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;
      const jwtToken =
        localStorage.getItem('authToken') ?? state.persistedReducer.token;

      if (jwtToken) headers.set('Authorization', `Bearer ${jwtToken}`);
      headers.set('ngrok-skip-browser-warning', 'true');
      return headers;
    }
  });

  const baseQueryFn: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    api.dispatch(setLoadingCall(true));

    try {
      await mutex.waitForUnlock();

      let result = await baseQuery(args, api, extraOptions);

      if (result.error && result.error.status === ErrorRequest.badRequest) {
        const err = result.error as any;
        const message = err?.data?.message;
        if (message) toast.error(message);
      }

      if (result.error && result.error.status === ErrorRequest.expiredToken) {
        const tokenUsed =
          localStorage.getItem('authToken') ??
          (api.getState() as RootState).persistedReducer.token ??
          '';

        if (!mutex.isLocked()) {
          const release = await mutex.acquire();
          try {
            const tokenNow = localStorage.getItem('authToken') ?? '';

            if (tokenNow && tokenUsed && tokenNow !== tokenUsed) {
              result = await baseQuery(args, api, extraOptions);
              return result;
            }

            const state = api.getState() as RootState;
            const currentUser = selectCurrentUser(state);

            const storedToken =
              localStorage.getItem('authToken') ?? currentUser.token ?? '';
            const storedRefreshToken =
              localStorage.getItem('refreshToken') ??
              (currentUser as any)?.refreshToken ??
              '';

            if (!storedToken || !storedRefreshToken) return result;

            let userId: number | null = null;
            try {
              const decoded: any = jwtDecode(storedToken);
              const uniqueName = decoded?.['unique_name'];
              userId =
                typeof uniqueName === 'number'
                  ? uniqueName
                  : Number(uniqueName);
              if (!Number.isFinite(userId)) userId = null;
            } catch {
              userId = null;
            }

            if (userId === null) return result;

            const refreshResult = await baseQuery(
              {
                url: 'api/Auth/Refresh',
                method: 'POST',
                body: { userId, refreshToken: storedRefreshToken }
              },
              api,
              extraOptions
            );

            if (refreshResult.data) {
              const data = refreshResult.data as RefreshApiTokenResponse;

              localStorage.setItem('authToken', data.token);
              localStorage.setItem('refreshToken', data.refreshToken);

              api.dispatch(
                setTokenAndRefreshToken({
                  token: data.token,
                  refreshToken: data.refreshToken
                })
              );

              api.dispatch(
                setCredentials({
                  user: currentUser.user ?? data.username ?? '',
                  email: currentUser.email ?? '',
                  token: data.token,
                  picture: currentUser.picture ?? '',
                  roleInfo: currentUser.roleInfo ?? {},
                  userInfo: currentUser.userInfo ?? null,
                  refreshToken: data.refreshToken,
                  authWithApi: true,
                  userId
                })
              );

              result = await baseQuery(args, api, extraOptions);
            } else {
              localStorage.removeItem('authToken');
              localStorage.removeItem('refreshToken');
              window.location.href = '/login-with-password';
            }
          } finally {
            release();
          }
        } else {
          await mutex.waitForUnlock();
          result = await baseQuery(args, api, extraOptions);
        }
      }

      return result;
    } finally {
      api.dispatch(setLoadingCall(false));
    }
  };

  return baseQueryFn;
};

export default customFetchBase;
